/**
 * AnniversaryOptIn — tiny inline checkbox rendered on the memorial
 * sidebar for signed-in followers. Toggles the follower's
 * `every_anniversary` preference on
 *   PATCH /api/memorials/{slug}/follow-preferences
 * so they receive an email each year on the death anniversary with a
 * link to leave a fresh candle.
 *
 * Loads the current pref once on mount via GET follow-preferences and
 * saves optimistically on toggle.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Cake } from "lucide-react";

export default function AnniversaryOptIn({ slug, fullName }) {
  const [checked, setChecked] = useState(null);   // null while loading
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    let alive = true;
    http.get(`/memorials/${slug}/follow-preferences`)
      .then((r) => {
        if (!alive) return;
        const p = r.data?.prefs || {};
        setChecked(p.every_anniversary ?? true);   // default on for followers
      })
      .catch(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, [slug]);

  const toggle = async (v) => {
    setBusy(true);
    setChecked(v);
    try {
      // Fetch current, mutate the flag, PATCH the full object back.
      const cur = (await http.get(`/memorials/${slug}/follow-preferences`)).data?.prefs || {};
      await http.patch(`/memorials/${slug}/follow-preferences`, {
        ...cur, every_anniversary: v,
      });
      toast.success(v
        ? `We'll email you every anniversary of ${fullName || "them"}.`
        : "Anniversary reminders paused.");
    } catch (e) {
      setChecked(!v);
      // FastAPI 422 responses carry `detail` as an array of error objects
      // which sonner cannot render; coerce to a friendly string.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn't update reminder setting.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (checked == null) return null;
  return (
    <label
      className="mt-2 flex items-start gap-2 text-xs cursor-pointer rounded-lg border p-2.5"
      style={{ borderColor: "var(--wofy-border)", background: "#FFFBEB" }}
      data-testid="mem-anniv-optin"
    >
      <input
        type="checkbox"
        checked={!!checked}
        disabled={busy}
        onChange={(e) => toggle(e.target.checked)}
        className="mt-0.5"
        data-testid="mem-anniv-optin-checkbox"
      />
      <span className="flex-1">
        <span className="font-medium inline-flex items-center gap-1">
          <Cake className="w-3 h-3" /> Anniversary reminder
        </span>
        <span className="block text-[11px] opacity-70 mt-0.5">
          Email me each year on the death anniversary with a link to leave a fresh candle.
        </span>
      </span>
    </label>
  );
}
